import type { Allowlist, AllowlistEntry } from "@phone-booth/content/allowlist";
import { titleFromName } from "@phone-booth/content/layout";
import type { Manifest, Message } from "@phone-booth/content/manifest";
import type { SiteConfig } from "./oauth.ts";
import { idToken } from "./session.ts";

export type { Allowlist, AllowlistEntry, Manifest, Message };

export interface Whoami {
  email: string;
  admin: boolean;
}

let config: SiteConfig | undefined;

/**
 * Called once, when the page has read config.json. Screens then just call the
 * functions below, rather than passing the configuration down through every
 * component that happens to sit above the one making the request.
 */
export function configure(site: SiteConfig): void {
  config = site;
}

function configured(): SiteConfig {
  if (!config) throw new Error("the admin API was called before it was configured");
  return config;
}

/** The API's refusals carry a plain-language reason; show it rather than a code. */
async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const site = configured();
  const token = await idToken(site);
  const response = await fetch(new URL(path, site.apiUrl), {
    ...init,
    headers: {
      ...init.headers,
      Authorization: `Bearer ${token}`,
      ...(init.body ? { "Content-Type": "application/json" } : {}),
    },
  });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const reason =
      typeof body === "object" && body !== null && "error" in body
        ? String((body as { error: unknown }).error)
        : `request failed (${response.status})`;
    throw new Error(reason);
  }
  return body as T;
}

export function whoami(): Promise<Whoami> {
  return call<Whoami>("whoami");
}

export function getAllowlist(): Promise<Allowlist> {
  return call<Allowlist>("allowlist");
}

export function putEntry(entry: AllowlistEntry): Promise<Allowlist> {
  return call<Allowlist>("allowlist", {
    method: "PUT",
    body: JSON.stringify(entry),
  });
}

export function removeEntry(email: string): Promise<Allowlist> {
  return call<Allowlist>(`allowlist?email=${encodeURIComponent(email)}`, {
    method: "DELETE",
  });
}

/** Everything the message screens draw, in one request. */
export interface Library {
  digits: Record<string, string>;
  categories: Manifest[];
}

/**
 * What to call a category on screen. A digit can point at a category the
 * library has no manifest for, so the short name stands in rather than a blank.
 */
export function categoryTitle(library: Library, category: string): string {
  const found = library.categories.find((entry) => entry.category === category);
  return found?.title ?? titleFromName(category);
}

export interface VoiceOption {
  id: string;
  name: string;
  description: string;
}

/** The voice list, what the booth has curated out, and the month's budget. */
export interface VoiceList {
  voices: VoiceOption[];
  /** The name of the voice the engine speaks in when nobody picks one. */
  defaultVoice?: string;
  /** Voice ids staff may not pick. Absent on an API deployed before the catalog. */
  disabled?: string[];
  credits?: { used: number; limit: number };
}

export interface Spoken {
  audio: string;
  contentType: string;
  voice: VoiceOption;
}

/** One URL, good for one PUT of one file of one type. */
export interface UploadTicket {
  id: string;
  masterKey: string;
  contentType: string;
  maxBytes: number;
  url: string;
}

export function getLibrary(): Promise<Library> {
  return call<Library>("library");
}

/**
 * Creates a topic, renames one, or turns its number on and off. Leaving
 * `enabled` off keeps whatever the topic already had, so a rename cannot put a
 * disabled number back into service.
 */
export function saveCategory(
  category: string,
  title: string,
  enabled?: boolean,
): Promise<Manifest> {
  return call<Manifest>("category", {
    method: "POST",
    body: JSON.stringify({
      category,
      title,
      ...(enabled === undefined ? {} : { enabled }),
    }),
  });
}

/** Points a digit at the topic set up on it. A topic never moves off its digit. */
export function setRoute(
  digit: string,
  category: string,
): Promise<{ digits: Record<string, string> }> {
  return call("route", {
    method: "PUT",
    body: JSON.stringify({ digit, category }),
  });
}

/**
 * Renames a message, turns it on or off, or both. Neither change touches the
 * audio, so a rename costs nothing. It is the only edit a recording can have,
 * and the cheap path when a typed message keeps its words.
 */
export function editMessage(
  category: string,
  id: string,
  changes: { title?: string; active?: boolean },
): Promise<Manifest> {
  return call<Manifest>("message", {
    method: "PATCH",
    body: JSON.stringify({ category, id, ...changes }),
  });
}

export function deleteMessage(category: string, id: string): Promise<Manifest> {
  const query = `category=${encodeURIComponent(category)}&id=${encodeURIComponent(id)}`;
  return call<Manifest>(`message?${query}`, { method: "DELETE" });
}

export function getVoices(): Promise<VoiceList> {
  return call<VoiceList>("voices");
}

/** Turns one voice off or back on for everyone; answers the new disabled set. */
export function setVoiceEnabled(
  voice: string,
  enabled: boolean,
): Promise<{ disabled: string[] }> {
  return call("voices", {
    method: "PUT",
    body: JSON.stringify({ voice, enabled }),
  });
}

/**
 * Reads text into a stored message. `active: false` is how the admin site adds
 * one: nobody hears it until a person has listened and enabled it.
 *
 * Passing the `id` of a message that already exists is how a typed message is
 * edited. The words are read again in the voice given, the audio is replaced
 * under the same key, and the entry keeps the day it first appeared. Send the
 * message's current `active` with it — the API defaults a missing one to true,
 * which would put a disabled message back on the phone.
 */
export function addTypedMessage(request: {
  category: string;
  id?: string;
  title: string;
  text: string;
  voice?: string | undefined;
  active?: boolean;
}): Promise<{ manifest: Manifest; message: Message }> {
  return call("message/tts", { method: "POST", body: JSON.stringify(request) });
}

/** Speaks the text without storing it. This spends credits; say so first. */
export function previewSpeech(text: string, voice?: string): Promise<Spoken> {
  return call<Spoken>("message/preview", {
    method: "POST",
    body: JSON.stringify({ text, voice }),
  });
}

export function startUpload(request: {
  category: string;
  title: string;
  contentType: string;
}): Promise<UploadTicket> {
  return call<UploadTicket>("upload", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/**
 * Straight to S3, with no token on it: the signature in the URL is what lets
 * this through. Only the content type is sent — it is the one signed header;
 * the pending-upload tag rides in the URL's query string, already signed.
 */
export async function putMaster(ticket: UploadTicket, audio: Blob): Promise<void> {
  const response = await fetch(ticket.url, {
    method: "PUT",
    headers: { "Content-Type": ticket.contentType },
    body: audio,
  });
  if (!response.ok) throw new Error(`the upload failed (${response.status})`);
}

export function finishUpload(request: {
  category: string;
  id: string;
  title: string;
  masterKey: string;
  active?: boolean;
}): Promise<{ manifest: Manifest; message: Message }> {
  return call("upload/complete", { method: "POST", body: JSON.stringify(request) });
}

/** A URL the browser can play this message from, good for a few minutes. */
export function audioUrl(
  category: string,
  id: string,
  file: "master" | "phone",
): Promise<{ url: string }> {
  const query =
    `category=${encodeURIComponent(category)}&id=${encodeURIComponent(id)}` +
    `&file=${encodeURIComponent(file)}`;
  return call(`audio?${query}`);
}
